<template>
    <canvas ref="canvas" class="season-sky" aria-hidden="true"></canvas>
</template>

<script>
// Céu de festas num canvas que preenche o elemento pai (menu lateral, cabeçalho, cartão, painel do login). O pai
// precisa de position e overflow: hidden.
// Natal: neve em três profundidades (a de perto desfocada, como bokeh) e, de vez em quando, um brilho que pisca.
// Vagalumes (prop fireflies): luzinhas quentes que vagueiam devagar e piscam, cada uma no seu ritmo.
// Réveillon (prop confetti): confete dourado e champanhe que gira e vira, mais algumas serpentinas.
// Pausa com a aba em segundo plano e fora da tela; com "reduzir movimento" não anima.
import {prefersReducedMotion} from '@/Helpers/global/season';

const rand = (min, max) => min + Math.random() * (max - min);
const pick = list => list[Math.floor(Math.random() * list.length)];

// Ponto de luz pronto (desenhar imagem é bem mais barato que gradiente por quadro)
function sprite(size, stops) {
    const canvas = document.createElement('canvas');
    canvas.width = canvas.height = size;
    const ctx = canvas.getContext('2d');
    const r = size / 2;
    const gradient = ctx.createRadialGradient(r, r, 0, r, r, r);
    stops.forEach(([at, color]) => gradient.addColorStop(at, color));
    ctx.fillStyle = gradient;
    ctx.fillRect(0, 0, size, size);
    return canvas;
}

function buildSprites(shaded) {
    const flake = shaded
        // Fundo claro: o floco leva um halo esmeralda suave (a cor da marca) para aparecer
        ? [[0, 'rgba(255,255,255,1)'], [0.38, 'rgba(255,255,255,0.95)'], [0.55, 'rgba(14,94,78,0.28)'], [1, 'rgba(14,94,78,0)']]
        : [[0, 'rgba(255,255,255,1)'], [0.42, 'rgba(255,255,255,0.92)'], [0.66, 'rgba(255,255,255,0.32)'], [1, 'rgba(255,255,255,0)']];
    return {
        flake: sprite(32, flake),
        bokeh: sprite(64, [[0, 'rgba(255,255,255,0.5)'], [0.6, 'rgba(255,255,255,0.22)'], [1, 'rgba(255,255,255,0)']]),
        glint: sprite(32, [[0, 'rgba(255,248,220,1)'], [0.25, 'rgba(246,220,150,0.8)'], [1, 'rgba(246,220,150,0)']]),
        // Vagalume: miolo claro amarelo-esverdeado com halo largo
        firefly: sprite(48, [[0, 'rgba(255,255,225,1)'], [0.12, 'rgba(240,255,160,0.95)'], [0.32, 'rgba(205,240,90,0.45)'],
            [1, 'rgba(190,230,70,0)']]),
    };
}

// Confete: champanhe e dourado; no fundo claro, dourados mais fundos e um toque de esmeralda
const CONFETTI_LIGHT = ['#c8973c', '#b8862f', '#d9ad55', '#a87a2a', '#0e5e4e', '#e2b85c'];
const CONFETTI_DARK = ['#f3d68a', '#e2b85c', '#fff4cf', '#d9ad55', '#f6e3a8', '#5eead4'];

export default {
    name: 'SeasonSky',
    props: {
        // Flocos a cada 10.000 px² do elemento
        density: {type: Number, default: 1},
        // Fundo claro: halo no floco e confete mais escuro
        shaded: {type: Boolean, default: false},
        maxSize: {type: Number, default: 6},
        speed: {type: Number, default: 1},
        // Réveillon: confete no lugar da neve
        confetti: {type: Boolean, default: false},
        // Flocos grandes e desfocados na frente (só em áreas grandes, como o painel do login)
        bokeh: {type: Boolean, default: false},
        // Brilhos dourados que piscam parados (estrelas)
        glints: {type: Number, default: 0},
        // Vagalumes que vagueiam e piscam (quantidade)
        fireflies: {type: Number, default: 0},
    },
    watch: {
        density() {
            if (this.ctx) this.resize();
        },
        shaded(value) {
            if (!this.ctx) return;
            this.sprites = buildSprites(value);
            this.flakes.forEach(flake => (flake.color = pick(value ? CONFETTI_LIGHT : CONFETTI_DARK)));
        },
    },
    mounted() {
        if (prefersReducedMotion()) return;
        this.ctx = this.$refs.canvas.getContext('2d');
        this.sprites = buildSprites(this.shaded);
        this.flakes = [];
        this.stars = [];
        this.bugs = [];
        this.visible = true;
        this.clock = 0;
        this.last = performance.now();
        this.tick = this.tick.bind(this);
        this.restart = this.restart.bind(this);
        this.resizeObserver = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(() => this.resize()) : null;
        this.resizeObserver?.observe(this.$el.parentElement);
        // Fora da tela (menu recolhido, cartão rolado para longe) não desenha
        this.intersection = typeof IntersectionObserver !== 'undefined' ? new IntersectionObserver(([entry]) => {
            this.visible = entry.isIntersecting;
            this.restart();
        }) : null;
        this.intersection?.observe(this.$el);
        document.addEventListener('visibilitychange', this.restart);
        this.motionQuery = window.matchMedia ? window.matchMedia('(prefers-reduced-motion: reduce)') : null;
        this.motionQuery?.addEventListener?.('change', this.restart);
        this.resize();
        this.restart();
    },
    beforeUnmount() {
        cancelAnimationFrame(this.frame);
        this.resizeObserver?.disconnect();
        this.intersection?.disconnect();
        if (this.ctx) {
            document.removeEventListener('visibilitychange', this.restart);
            this.motionQuery?.removeEventListener?.('change', this.restart);
        }
    },
    methods: {
        makeFlake(anywhere) {
            const near = this.bokeh && !this.confetti && Math.random() > 0.95;
            const depth = near ? rand(1.25, 1.7) : Math.pow(Math.random(), 1.35);
            const size = near ? rand(14, 26) : 1.4 + depth * (this.maxSize - 1.4);
            return {
                near,
                x: Math.random() * this.width,
                y: anywhere ? Math.random() * this.height : rand(-30, -6),
                depth,
                size: this.confetti ? size * 1.15 : size,
                fall: (near ? 34 : 8 + depth * 26) * this.speed * (this.confetti ? 1.15 : 1),
                sway: rand(4, 12) * (0.4 + depth),
                swaySpeed: rand(0.3, 0.8),
                phase: Math.random() * Math.PI * 2,
                alpha: near ? rand(0.25, 0.45) : 0.5 + depth * 0.5,
                color: pick(this.shaded ? CONFETTI_LIGHT : CONFETTI_DARK),
                angle: Math.random() * Math.PI * 2,
                spin: rand(-2.6, 2.6),
                flip: rand(2.5, 5.5),
                // Serpentina: tira longa ondulando (1 em 9 peças de confete)
                ribbon: this.confetti && Math.random() > 0.89,
            };
        },
        makeStar() {
            return {
                x: Math.random() * this.width,
                y: Math.random() * this.height * 0.75,
                size: rand(8, 16),
                period: rand(3.5, 7),
                phase: Math.random() * 10,
            };
        },
        makeFirefly() {
            return {
                x: Math.random() * this.width,
                y: rand(0.1, 0.95) * this.height,
                heading: Math.random() * Math.PI * 2,
                speed: rand(10, 24),
                turn: rand(0.4, 1.1),
                turnPhase: Math.random() * 10,
                size: rand(20, 34),
                // Pisca: acende rápido, apaga devagar e fica um tempo no escuro
                period: rand(2.4, 5.5),
                phase: Math.random() * 10,
            };
        },
        drawFireflies(dt) {
            const {ctx, width, height, sprites} = this;
            for (const bug of this.bugs) {
                // Voo errante: o rumo ondula com senos de frequências diferentes
                bug.heading += Math.sin(this.clock * bug.turn + bug.turnPhase) * 1.6 * dt;
                bug.x += Math.cos(bug.heading) * bug.speed * dt;
                bug.y += Math.sin(bug.heading) * bug.speed * dt * 0.7;
                // Sai por um lado, volta pelo outro
                if (bug.x < -30) bug.x = width + 30;
                else if (bug.x > width + 30) bug.x = -30;
                if (bug.y < -30) bug.y = height + 30;
                else if (bug.y > height + 30) bug.y = -30;
                const t = ((this.clock + bug.phase) % bug.period) / bug.period;
                const glow = t < 0.12 ? t / 0.12 : t < 0.5 ? 1 - (t - 0.12) / 0.38 : 0;
                // Apagado ainda deixa um pontinho quase invisível
                const alpha = 0.08 + glow * 0.92;
                const size = bug.size * (0.55 + glow * 0.45);
                ctx.globalAlpha = alpha;
                ctx.drawImage(sprites.firefly, bug.x - size / 2, bug.y - size / 2, size, size);
            }
        },
        resize() {
            const parent = this.$el.parentElement;
            const canvas = this.$refs.canvas;
            if (!parent || !canvas || !this.ctx) return;
            const ratio = Math.min(window.devicePixelRatio || 1, 2);
            this.width = parent.clientWidth;
            this.height = parent.clientHeight;
            canvas.width = Math.round(this.width * ratio);
            canvas.height = Math.round(this.height * ratio);
            this.ctx.setTransform(ratio, 0, 0, ratio, 0, 0);
            // Confete é mais vistoso que a neve: menos peças
            const density = this.confetti ? this.density * 0.5 : this.density;
            const target = Math.round(Math.min(240, (this.width * this.height) / 10000 * density));
            while (this.flakes.length < target) this.flakes.push(this.makeFlake(true));
            this.flakes.length = target;
            while (this.stars.length < this.glints) this.stars.push(this.makeStar());
            this.stars.forEach(star => {
                if (star.x > this.width || star.y > this.height) Object.assign(star, this.makeStar());
            });
            while (this.bugs.length < this.fireflies) this.bugs.push(this.makeFirefly());
            this.bugs.length = this.fireflies;
        },
        restart() {
            cancelAnimationFrame(this.frame);
            if (this.ctx && this.visible && !document.hidden && !this.motionQuery?.matches) {
                this.last = performance.now();
                this.frame = requestAnimationFrame(this.tick);
            }
        },
        drawConfetti(flake, x, dt) {
            const {ctx} = this;
            flake.angle += flake.spin * dt;
            ctx.save();
            ctx.translate(x, flake.y);
            ctx.rotate(flake.angle);
            ctx.fillStyle = flake.color;
            if (flake.ribbon) {
                // Serpentina: curva em S que ondula
                const wave = Math.sin(this.clock * flake.flip + flake.phase) * flake.size * 0.5;
                ctx.strokeStyle = flake.color;
                ctx.lineWidth = Math.max(1, flake.size * 0.22);
                ctx.lineCap = 'round';
                ctx.beginPath();
                ctx.moveTo(0, -flake.size * 1.4);
                ctx.bezierCurveTo(wave, -flake.size * 0.5, -wave, flake.size * 0.5, 0, flake.size * 1.4);
                ctx.stroke();
            } else {
                // Papel girando em 3D: a altura acompanha o cosseno do giro
                const w = flake.size * 0.55;
                const h = flake.size * 1.05 * Math.abs(Math.cos(this.clock * flake.flip + flake.phase));
                ctx.fillRect(-w / 2, -h / 2, w, Math.max(0.6, h));
            }
            ctx.restore();
        },
        tick(now) {
            const dt = Math.min(0.05, (now - this.last) / 1000);
            this.last = now;
            this.clock += dt;
            const {ctx, width, height, sprites} = this;
            ctx.clearRect(0, 0, width, height);

            for (const star of this.stars) {
                // Pisca suave: aparece, brilha e some, cada um no seu ritmo
                const t = ((this.clock + star.phase) % star.period) / star.period;
                const glow = Math.max(0, Math.sin(t * Math.PI * 2)) ** 3;
                if (glow < 0.02) continue;
                const size = star.size * (0.6 + glow * 0.6);
                ctx.globalAlpha = glow * 0.9;
                ctx.drawImage(sprites.glint, star.x - size / 2, star.y - size / 2, size, size);
            }

            this.drawFireflies(dt);

            const wind = Math.sin(this.clock * 0.23) * 6 + Math.sin(this.clock * 0.09) * 4;
            for (const flake of this.flakes) {
                flake.y += flake.fall * dt;
                flake.x += wind * (0.4 + flake.depth) * dt;
                const x = flake.x + Math.sin(this.clock * flake.swaySpeed + flake.phase) * flake.sway;
                if (flake.y - flake.size > height || x < -40 || x > width + 40) {
                    Object.assign(flake, this.makeFlake(false));
                    continue;
                }
                ctx.globalAlpha = flake.alpha;
                if (this.confetti) {
                    this.drawConfetti(flake, x, dt);
                } else {
                    const image = flake.near ? sprites.bokeh : sprites.flake;
                    ctx.drawImage(image, x - flake.size / 2, flake.y - flake.size / 2, flake.size, flake.size);
                }
            }
            ctx.globalAlpha = 1;
            this.frame = requestAnimationFrame(this.tick);
        },
    },
};
</script>

<style>
.season-sky {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    pointer-events: none;
}

@media (prefers-reduced-motion: reduce) {
    .season-sky {
        display: none;
    }
}
</style>
